import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DriverConceptView, DriverView, DriversView } from '../../types/agent';
import { CellText } from './CellLink';

/** The drivers of a number (sculpt-api PLAN-pf-skills.md §6): every Assumption upstream of it, folded behind one
 *  button under the model's answer until the user opens it (the answer explains the drivers that matter; the card
 *  is the complete list, on request). Opened, the business ones lead,
 *  as the tree of the Zones they sit in (a concept: a PPA, an O&M contract, a tax, with its role), each with its
 *  value and a link selecting its cells; a concept's "select" selects its drivers of one sheet at once. Then the
 *  numbers typed in formulas, the pasted values, the timing (folded: it is long and rarely the question), the values
 *  from other workbooks and the calculations walked through, counted. Drawn from the `drivers` tool's card — the
 *  model writes the explanation above it — so the list is complete and costs no tokens. With a period asked, the
 *  drivers that do not move the number there (dormant: a flag at 0, a `MIN` argument not taken) leave the lists and
 *  come after the business ones, grouped under what leaves them out. The places named in words — what leaves a driver
 *  out (`Operations!V10 (PPA flag)`), the target, the cells the walk could not follow — are links (`CellText`). */
export function DriversCard({ view, onShowCell, sheets }: {
  view: DriversView;
  onShowCell?: (sheet: string, cell: string) => void;
  /** The workbook's sheet names: a place in the card's words is a link only when it names one of them. */
  sheets?: readonly string[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [timingOpen, setTimingOpen] = useState(false);
  const c = view.counts;
  const calculations = Object.entries(view.calculations);
  const total = c.business + c.timing + c.pasted + c.lookup_tables;
  const dormant = view.dormant ?? [];
  const dormantCount = dormant.reduce((n, g) => n + g.drivers.length, 0);
  const concepts = view.concepts.map(activeOnly).filter((n): n is DriverConceptView => n !== null);
  if (!open) {
    return (
      <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="drivers-card">
        <button type="button" data-testid="drivers-open" onClick={() => setOpen(true)}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-ink hover:bg-hover">
          <span className="text-muted">▸</span>
          <span>{t('drivers.show', { drivers: total, calculations: c.calculations })}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="drivers-card">
      <div className="border-b border-line-row px-3 py-2">
        <button type="button" data-testid="drivers-close" onClick={() => setOpen(false)}
          className="flex w-full items-center gap-1.5 text-left text-[12.5px] font-medium text-ink">
          <span className="text-muted">▾</span>
          <span>{t('drivers.title')}</span>
        </button>
        <div className="mt-0.5 wrap-break-word text-[11.5px] text-muted" data-testid="drivers-subject"><CellText text={view.subject} sheets={sheets} onSelectRange={onShowCell} /></div>
        <div className="mt-0.5 text-[11px] text-muted">
          {t('drivers.counts', { business: c.business, timing: c.timing, pasted: c.pasted })}
        </div>
        {dormant.length > 0 && (
          <div className="mt-0.5 text-[11px] text-muted">{t('drivers.dormantCount', { count: dormantCount })}</div>
        )}
        {view.loop && <div className="mt-0.5 text-[11px] text-amber-800">{t('drivers.loop')}</div>}
      </div>

      <Section title={t('drivers.business')}>
        {concepts.length === 0 && <div className="px-3 py-1.5 text-[11.5px] text-muted">{t('drivers.none')}</div>}
        {concepts.map((n) => <Concept key={n.path.join('›')} node={n} depth={0} onShowCell={onShowCell} />)}
      </Section>

      {dormant.length > 0 && (
        <Section title={view.period ? t('drivers.dormant', { period: view.period }) : t('drivers.dormantAny')}
          note={t('drivers.dormantNote')} testId="drivers-dormant">
          {dormant.map((g) => (
            <div key={g.wake} className="pb-1" data-testid="drivers-dormant-group">
              <div className="wrap-break-word px-3 pt-1 text-[11px] text-muted"><CellText text={g.wake} sheets={sheets} onSelectRange={onShowCell} /></div>
              {g.drivers.map((d) => <DriverRow key={d.id} d={d} onShowCell={onShowCell} indent />)}
            </div>
          ))}
        </Section>
      )}

      {view.tables.length > 0 && (
        <Section title={t('drivers.tables')} note={t('drivers.tablesNote')} testId="drivers-tables">
          {view.tables.map((tb) => (
            <Row key={tb.at} name={tb.heading} value={t('drivers.entries', { count: tb.entries })} sheet={tb.sheet} address={tb.address} onShowCell={onShowCell} />
          ))}
        </Section>
      )}

      {(view.in_formulas.length > 0 || view.formula_inputs.length > 0) && (
        <Section title={t('drivers.inFormulas')} testId="drivers-in-formulas">
          {view.in_formulas.map((f) => (
            <Row key={`${f.id}-${f.text}`} name={`${f.label ?? f.id}`} value={f.text} sheet={f.sheet} address={f.address} onShowCell={onShowCell} />
          ))}
          {view.formula_inputs.map((f) => (
            <Row key={f.id} name={f.label ?? f.id} value={f.formula} sheet={f.sheet} address={f.address} onShowCell={onShowCell} />
          ))}
        </Section>
      )}

      {view.pasted.length > 0 && (
        <Section title={t('drivers.pasted')} note={t('drivers.pastedNote')} testId="drivers-pasted">
          {view.pasted.filter(isActive).map((d) => <DriverRow key={d.id} d={d} onShowCell={onShowCell} />)}
          {view.more_pasted > 0 && <More count={view.more_pasted} />}
        </Section>
      )}

      {view.timing.length > 0 && (
        <div className="border-t border-line-row">
          <button type="button" data-testid="drivers-timing-toggle" onClick={() => setTimingOpen(!timingOpen)}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[11.5px] font-medium text-ink hover:bg-hover">
            <span>{t('drivers.timing', { count: c.timing })}</span>
            <span className="text-muted">{timingOpen ? '−' : '+'}</span>
          </button>
          {timingOpen && view.timing.map((g) => (
            <div key={g.heading} className="px-3 pb-1.5" data-testid="drivers-timing-group">
              <div className="flex min-w-0 items-baseline gap-1.5 text-[11px]">
                <span className="truncate text-ink">{g.heading}</span>
                <Place sheet={g.sheet} address={g.address} onSelect={onShowCell} />
              </div>
              {g.drivers.filter(isActive).map((d) => <DriverRow key={d.id} d={d} onShowCell={onShowCell} indent />)}
              {g.count > g.drivers.length && <div className="pl-2 text-[11px] text-muted">{t('drivers.timingRolled', { count: g.count })}</div>}
            </div>
          ))}
        </div>
      )}

      {(view.externals.length > 0 || view.unresolved.length > 0) && (
        <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-muted">
          {view.externals.length > 0 && <div>{t('drivers.externals', { refs: view.externals.slice(0, 3).join(', ') })}</div>}
          {view.unresolved.length > 0 && (
            <div className="text-amber-800">
              <CellText text={t('drivers.unresolved', { cells: view.unresolved.slice(0, 3).join(', ') })} sheets={sheets} onSelectRange={onShowCell} />
            </div>
          )}
        </div>
      )}

      <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-muted" data-testid="drivers-calculations">
        {t('drivers.calculations', { count: c.calculations, sheets: calculations.slice(0, 3).map(([s, n]) => `${s} ${n}`).join(', ') })}
      </div>
    </div>
  );
}

const isActive = (d: DriverView) => d.effect !== 'dormant';

/** A concept without its dormant drivers, or null when nothing is left to show (no active driver, none past the cap). */
function activeOnly(node: DriverConceptView): DriverConceptView | null {
  const drivers = node.drivers.filter(isActive);
  const children = node.children.map(activeOnly).filter((n): n is DriverConceptView => n !== null);
  if (!drivers.length && !children.length && !node.more) return null;
  return { ...node, drivers, children };
}

function Section({ title, note, testId, children }: { title: string; note?: string; testId?: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-line-row py-1 first:border-t-0" data-testid={testId}>
      <div className="px-3 pt-1 text-[11.5px] font-medium text-ink">{title}</div>
      {note && <div className="px-3 text-[11px] text-muted">{note}</div>}
      {children}
    </div>
  );
}

/** Every driver of a node and below, on the sheet of its first one: what "select" selects in Excel at once. */
function addressesOf(node: DriverConceptView): { sheet: string; addresses: string[] } | null {
  const all: DriverView[] = [];
  const walk = (n: DriverConceptView) => { all.push(...n.drivers); n.children.forEach(walk); };
  walk(node);
  if (!all.length) return null;
  const sheet = all[0].sheet;
  return { sheet, addresses: all.filter((d) => d.sheet === sheet).map((d) => d.address) };
}

function Concept({ node, depth, onShowCell }: { node: DriverConceptView; depth: number; onShowCell?: (sheet: string, cell: string) => void }) {
  const { t } = useTranslation();
  const all = addressesOf(node);
  return (
    <div style={{ paddingLeft: depth ? 10 : 0 }} data-testid="drivers-concept">
      <div className="flex min-w-0 items-baseline gap-1.5 px-3 pt-1 text-[11.5px]">
        <span className="shrink-0 font-medium text-ink">{node.heading}</span>
        {node.role && <span className="min-w-0 truncate text-muted" title={node.role}>{node.role}</span>}
        {onShowCell && all && all.addresses.length > 1 && (
          <button type="button" data-testid="drivers-select" title={all.addresses.join(', ')}
            onClick={() => onShowCell(all.sheet, all.addresses.join(','))}
            className="ml-auto shrink-0 text-[11px] text-accent hover:underline">{t('drivers.select')}</button>
        )}
      </div>
      {node.drivers.map((d) => <DriverRow key={d.id} d={d} onShowCell={onShowCell} indent />)}
      {node.more > 0 && <More count={node.more} />}
      {node.children.map((k) => <Concept key={k.path.join('›')} node={k} depth={depth + 1} onShowCell={onShowCell} />)}
    </div>
  );
}

/** The drivers past the card's cap: counted, and listed by asking the agent for that concept. */
function More({ count }: { count: number }) {
  const { t } = useTranslation();
  return <div className="py-0.5 pl-5 pr-3 text-[11px] text-muted" data-testid="drivers-more">{t('drivers.more', { count })}</div>;
}

function DriverRow({ d, onShowCell, indent }: { d: DriverView; onShowCell?: (sheet: string, cell: string) => void; indent?: boolean }) {
  const { t } = useTranslation();
  const value = `${d.value}${d.unit ? ` ${d.unit}` : ''}`;
  const share = d.total > 1 && d.cells < d.total ? t('drivers.share', { cells: d.cells, total: d.total }) : null;
  return <Row name={d.name} value={value} sheet={d.sheet} address={d.address} share={share} onShowCell={onShowCell} indent={indent} />;
}

function Row({ name, value, sheet, address, share, onShowCell, indent }: {
  name: string; value: string; sheet: string; address: string; share?: string | null;
  onShowCell?: (sheet: string, cell: string) => void; indent?: boolean;
}) {
  return (
    <div className={`flex min-w-0 items-baseline gap-1.5 py-0.5 pr-3 text-[11.5px] ${indent ? 'pl-5' : 'pl-3'}`} data-testid="drivers-row">
      <span className="min-w-0 flex-1 truncate text-ink" title={name}>{name}</span>
      <span className="shrink-0 font-mono text-[11px] text-ink" title={value}>{value.length > 24 ? `${value.slice(0, 23)}…` : value}</span>
      <Place sheet={sheet} address={address} onSelect={onShowCell} />
      {share && <span className="shrink-0 text-[10.5px] text-fainter">{share}</span>}
    </div>
  );
}

function Place({ sheet, address, onSelect }: { sheet: string; address: string; onSelect?: (sheet: string, address: string) => void }) {
  const label = `${sheet}!${address}`;
  if (!onSelect) return <span className="max-w-[45%] shrink-0 truncate font-mono text-[10.5px] text-muted" title={label}>{label}</span>;
  return (
    <button type="button" data-testid="drivers-place" onClick={() => onSelect(sheet, address)} title={label}
      className="max-w-[45%] shrink-0 truncate font-mono text-[10.5px] text-accent underline-offset-2 hover:underline">
      {label}
    </button>
  );
}
